/**
 * Pure **RAW activation selectors** (Core p.60 "Turns and Rounds").
 *
 * The activation machine is a pure reducer (`reducer.ts`) over pure derivations. The
 * primitive derivations that live on the state shape itself (`sideOf`, `unactedOnSide`,
 * `freshActivation`) stay in {@link ./types}; the *machine* selectors — cross-round
 * alternation, the per-unit UI state, round completion, and the "able" pool the legality
 * rules read — live here, the **single source** the reducer, the room, the CLI, and the
 * later two-column UI all import. Rules live once; surfaced many places (no re-derivation
 * downstream — that seam is what lets the UI *follow* the engine rather than re-litigate
 * the model).
 */

import type { Combatant, CombatantId, EncounterState, Side } from "./types";
import { ACTIONS_PER_TURN, activeActor, BRACED_STATUS, hasStatus, isActor, sideOf, unactedOnSide, UNPILOTED_STATUS, type TurnBudget } from "./types";

/** The other side. */
function opposite(side: Side): Side {
  return side === "pc" ? "npc" : "pc";
}

/**
 * Whether a combatant is **able** to take a turn — *alive* (`hp > 0`) **and not destroyed**.
 * Destruction is the carried `"Destroyed"` status marker, **not** `hp <= 0`: a mech reduced to 0 HP
 * takes a structure hit and overflows its HP back to full (Core "STRUCTURE DAMAGE"), and a
 * catastrophic table row can destroy a unit outright — so a destroyed unit routinely has `hp > 0`.
 * Checking only HP would leave a destroyed-but-not-yet-activated unit in the un-acted pool, which
 * deadlocks round-end. Both a dead (`hp === 0`) and a destroyed (`"Destroyed"`) un-acted unit are
 * *not* able, so neither blocks round-end nor is a legal `activate_unit` target. (A *deleted* unit
 * is gone from `order`/`combatants` entirely, so it never reaches this predicate —
 * {@link unactedOnSide} skips it by construction.) Status-based incapacity (SHUT DOWN, STUNNED) is
 * a later refinement — **except Unpiloted**, which excludes an empty mech from activation entirely:
 * RAW's control loop requires a pilot, so an Unpiloted mech is never a legal `activate_unit` target
 * and never blocks round-end, exactly like a Destroyed unit. Mount clears the status, restoring the
 * mech to the able pool.
 *
 * **`current.hp` is read unconditionally**, and that is safe: `current` is always present, on the
 * server's authoritative state and on a client's alike — masking is display-only, so
 * `projectEncounterFor` never strips `current` off a player's un-Scanned hostile. That matters
 * because the frontend applies this very reducer optimistically against a projection, inside a
 * click handler, *before* the command reaches the server: a throw here would silently swallow the
 * command.
 */
export function isAble(c: Combatant): boolean {
  return c.current.hp > 0 && !hasStatus(c.statuses, "Destroyed") && !hasStatus(c.statuses, UNPILOTED_STATUS);
}

/**
 * The **able** un-acted units on a side: {@link unactedOnSide} filtered to {@link isAble} AND
 * {@link "./types" isActor} — a placed object/deployable folds to the `"npc"` side via
 * {@link "./types" sideOf} but is never an activation participant, so it must not block
 * `roundComplete` or occupy the un-acted pool. This is the pool the round-end + alternation
 * *legality* read (a dead un-acted unit must not deadlock the round) — and, as of
 * `projects/activation-count-readouts.md`, also the pool every display reads: the CLI's
 * `un-acted:` name list and {@link activationCountsOnSide}'s `remaining` both derive from
 * this, never from the unfiltered `unactedOnSide`.
 */
export function ableUnactedOnSide(state: EncounterState, side: Side): Combatant[] {
  return unactedOnSide(state, side).filter(isAble).filter(isActor);
}

/**
 * **RAW cross-round alternation** — the one place it lives. The new round opens on
 * the side that did *not* take the last turn of the previous round (Core p.60: *"if one
 * side took the last turn in the previous round, the other side starts the new round"*).
 * `lastActedSide` is the side of the final unit to end its turn; `null` (a round nobody
 * acted in — e.g. an empty encounter) opens players-first. Even-sided fights alternate
 * back to players-first "for free"; only uneven sides differ (and correctly).
 */
export function openingSide(lastActedSide: Side | null): Side {
  return lastActedSide === null ? "pc" : opposite(lastActedSide);
}

/**
 * The side of the unit that ended the **last** turn this round (the final id in
 * `activated`), or `null` when nobody acted (or the last actor was removed and can no
 * longer be sided). `end_round` feeds this to {@link openingSide}.
 */
export function lastActedSide(state: EncounterState): Side | null {
  const activated = state.activation.activated;
  for (let i = activated.length - 1; i >= 0; i--) {
    const c = state.combatants[activated[i]!];
    if (c) return sideOf(c);
  }
  return null;
}

/**
 * Is the round **complete**? — no *able* un-acted units remain on either side, and nobody is
 * mid-turn. This is `end_round`'s legality gate. A dead un-acted unit does not block completion
 * (alive = able for v1, via {@link ableUnactedOnSide}).
 */
export function roundComplete(state: EncounterState): boolean {
  if (state.activation.turn.phase !== "idle") return false;
  return ableUnactedOnSide(state, "pc").length === 0 && ableUnactedOnSide(state, "npc").length === 0;
}

/**
 * The four per-unit activation states the UI renders:
 *   - `active` — this unit is mid-turn (`activation.activating`).
 *   - `activated` — this unit has fully ended its turn (in `activation.activated`).
 *   - `ready` — un-acted, on the active side, and **legal to activate right now**
 *     (nobody else mid-turn). The `READY ▶` affordance — it mirrors `activate_unit`'s
 *     legality exactly, so the UI never re-derives "who can go".
 *   - `on_deck` — un-acted but not yet activatable (wrong side, or a turn is in progress).
 */
export type UnitActivationState = "ready" | "on_deck" | "active" | "activated";

/**
 * Per-combatant {@link UnitActivationState}, keyed by id. The single source the activation UI binds
 * rows to — `ready` is true iff `activate_unit` would be legal for that unit (nobody mid-turn ∧ on
 * the active side ∧ able), so an affordance built off this map can never disagree with the
 * reducer's wall.
 *
 * **A placed object/deployable never gets an entry at all** — {@link "./types" isActor} skips it in
 * the loop entirely, rather than defaulting it to `on_deck`, so the activation UI/tracker literally
 * never renders a row for it (it's not a turn-taking participant).
 *
 * **This is LOAD-BEARING FOR LEGALITY, not just for display.** It was written as the UI's mirror
 * of `activate_unit`'s legality, but the encounter room now reads the `"ready"` arm as the input
 * to a *server* decision: a controlled, ready unit acting off-activation has an `activate_unit`
 * folded in front of its action, and a player-attributed caller whose unit is NOT ready is
 * rejected outright (`projects/pre-activation-affordances.md` D2, `backend/src/
 * encounterRoomCore.ts`'s `activationDisposition`). Two consequences for anyone editing here:
 * loosening the `"ready"` arm widens what the server will auto-activate, and a unit with no entry
 * at all (an object/deployable/**drone** — `isActor` skips them) is never `"ready"`, which is what
 * keeps a player-owned drone's ungated action-bar buttons meeting the reject arm.
 */
export function perUnitState(state: EncounterState): Record<CombatantId, UnitActivationState> {
  const { activated, activeSide } = state.activation;
  const acting = activeActor(state.activation);
  const acted = new Set(activated);
  const out: Record<CombatantId, UnitActivationState> = {};
  for (const [id, c] of Object.entries(state.combatants)) {
    if (!isActor(c)) continue;
    if (id === acting) out[id] = "active";
    else if (acted.has(id)) out[id] = "activated";
    else if (acting === null && sideOf(c) === activeSide && isAble(c)) out[id] = "ready";
    else out[id] = "on_deck";
  }
  return out;
}

/**
 * The live **party size** for ULTRA's `activations_pct` threshold — the number of **distinct
 * `controllerSlotId`** among PC-side ({@link sideOf} `"pc"`) combatants. Distinct-slots, not a raw
 * combatant count, so a player running a mech *and* an on-foot pilot combatant counts **once** (our
 * analogue of COMP/CON counting `type === "pilot"`). A missing `controllerSlotId`
 * (`undefined`/`null` — a GM-controlled PC-side ally) collapses to one shared bucket, so any number
 * of such allies also count once, together — an accepted edge (wrong counts in that comp are the
 * GM's to fix via `set_activation`).
 *
 * **Destroyed-inclusive, deliberately NOT filtered through {@link isAble}** — a destroyed combatant
 * stays in `state.combatants` (a `"Destroyed"` status; removal is a separate command), and counting
 * it keeps the party size **round-stable**: a mech dying mid-round doesn't silently drop an ULTRA
 * below its threshold and cost it the bonus turn it already had. Matches the table-level reading of
 * "party size" — a downed player is still a player at the table.
 *
 * **Known-and-accepted comp, not engineered around**: `set_controller` lets a GM re-home an
 * EXISTING PC mech onto a slot that already controls another PC mech, live, mid-fight — the `Set`
 * below collapses what were two distinct buckets into one, dropping the count by 1 the instant it
 * lands (which can drop an ULTRA below `activationsPctThreshold` mid-fight). Same class of rare
 * comp as the GM-owned-ally case one paragraph up — pinned in `reducer.test.ts`'s `pcCountFor`
 * describe block, not fixed.
 */
export function pcCountFor(state: EncounterState): number {
  const slots = new Set<string | null>();
  for (const c of Object.values(state.combatants)) {
    // {@link isActor}-filtered, exactly like {@link ableUnactedOnSide} above: "party size" counts
    // turn-taking units, never a deployed object/deployable/drone. Load-bearing because a deployed
    // object takes its DEPLOYER's side, so a PC-deployed cover with no `controllerSlotId` would
    // otherwise add a phantom `null` bucket and inflate the party by one — silently handing an
    // ULTRA the {@link maxActivations} `activationsPctThreshold` bonus turn.
    if (!isActor(c)) continue;
    if (sideOf(c) !== "pc") continue;
    slots.add(c.controllerSlotId ?? null);
  }
  return slots.size;
}

/**
 * The **maximum number of turns** combatant `c` may take this round — the single consumer is
 * `endTurn` (`../reducer`), which gates entry into `activated` on this.
 * `1 + extraActivations + (pct threshold met ? 1 : 0)`:
 *   - the RAW base of **1** turn every combatant gets;
 *   - `base.extraActivations ?? 0` — ELITE's flat `+1`;
 *   - **+1**, never scaled, iff `base.activationsPctThreshold` is set AND
 *     {@link pcCountFor}`(state) >= threshold` — ULTRA's `activations_pct` is a THRESHOLD bonus,
 *     not a multiplier (verified against COMP/CON's `BonusController`).
 *
 * **Derived live, not a frozen resolved count**: `base` is frozen (replay-stable) and
 * {@link pcCountFor} is a pure derivation over `state.combatants` (also replay-stable), so this is
 * spawn-order-proof and staleness-free — an ULTRA deployed before every PC is placed (the common
 * GM-builds-hostiles-first authoring order) still resolves against the party that's actually on the
 * board by the time its 2nd activation is decided, not a snapshot frozen too early.
 */
export function maxActivations(state: EncounterState, c: Combatant): number {
  const { base } = c;
  let max = 1 + (base.extraActivations ?? 0);
  if (base.activationsPctThreshold != null && pcCountFor(state) >= base.activationsPctThreshold) {
    max += 1;
  }
  return max;
}

/**
 * The **activation-count readout** for a side — `{ used, remaining, inProgress }`, the single
 * derivation the CLI's `(PC n left, NPC m left)` line and the browser round header's
 * `{used}/{used+remaining+inProgress}` counter both read (`projects/activation-count-readouts.md`
 * R2/R4). Counts **activations**, not units, and counts only {@link "./types" isActor}
 * combatants — an object/deployable/drone never contributes to any field, exactly like
 * {@link ableUnactedOnSide}.
 *
 * - `used` — Σ {@link "./model/turn" ActivationState.activationsUsed} over every `isActor`
 *   combatant on `side`, iterating **`state.combatants`**, never the tally's own keys: a
 *   combatant removed via `remove_combatant` can leave a stale `activationsUsed` entry
 *   behind, and a unit gone from `state.combatants` has no side to charge it to. Includes a
 *   **dead or Destroyed unit's past turns on purpose** — a turn already taken stays taken
 *   even if the unit dies before its next one; only its *future* turns disappear (via
 *   `remaining`, below).
 * - `remaining` — Σ over {@link ableUnactedOnSide}`(state, side)` of
 *   `max(0, maxActivations(state, c) − activationsUsed[c.id])`, **plus**, when the mid-turn
 *   actor ({@link "./model/turn" activeActor}) is on `side` and {@link isAble}, that actor's
 *   own `max(0, maxActivations − activationsUsed[actor.id] − 1)` — the turns it still owes
 *   *after* the one in progress. (`activationsUsed` increments only at `end_turn`, so the
 *   mid-turn actor's current activation is never in its own tally yet; the `− 1` accounts for
 *   it without waiting for the increment.) Clamped to `0`, never negative — a `set_activation`
 *   force-apply that leaves a unit's tally above its `maxActivations` reads as "owes none",
 *   not a negative count.
 * - `inProgress` — `1` if the mid-turn actor is on `side`, else `0` — **unconditional on
 *   {@link isAble}**, unlike the `remaining` addend above. A unit that drops to 0 HP or gets
 *   marked Destroyed mid-turn still owns the in-progress activation until `end_turn` (the CLI
 *   still prints `mid-turn: <name>` for it), so the rail denominator must not drop to
 *   `used/used` for that moment; only its *further* owed turns (the `remaining` addend) are
 *   gated on able.
 *
 * **Invariant** (pinned in `reducer.test.ts`): at any `state.activation.turn.phase === "idle"`
 * moment, `remaining === 0 && inProgress === 0` on **both** sides ⇔ {@link roundComplete}`(state)`
 * — every able un-acted unit's tally is strictly below its max (that is what keeps it out of
 * `activated`), so a nonempty {@link ableUnactedOnSide} always contributes a positive
 * `remaining`, and `roundComplete` is exactly "both able-unacted pools are empty and nobody is
 * mid-turn."
 */
export type ActivationCounts = {
  used: number;
  remaining: number;
  inProgress: number;
};

export function activationCountsOnSide(state: EncounterState, side: Side): ActivationCounts {
  const { activationsUsed } = state.activation;

  let used = 0;
  for (const c of Object.values(state.combatants)) {
    if (!isActor(c) || sideOf(c) !== side) continue;
    used += activationsUsed[c.id] ?? 0;
  }

  let remaining = 0;
  for (const c of ableUnactedOnSide(state, side)) {
    remaining += Math.max(0, maxActivations(state, c) - (activationsUsed[c.id] ?? 0));
  }

  let inProgress = 0;
  const actingId = activeActor(state.activation);
  if (actingId !== null) {
    const actor = state.combatants[actingId];
    if (actor !== undefined && sideOf(actor) === side) {
      inProgress = 1;
      if (isAble(actor)) {
        remaining += Math.max(0, maxActivations(state, actor) - (activationsUsed[actingId] ?? 0) - 1);
      }
    }
  }

  return { used, remaining, inProgress };
}

/**
 * The per-turn budget a fresh activation mints: actions to the allotment, move to Speed, empty use
 * ledgers, and an explicit zero `spent`. `spent` is optional on `TurnBudget` and absent reads as 0,
 * but it is minted explicitly so a refactor that spreads a prior budget in here cannot carry a stale
 * count into a turn that has spent nothing. The single mint `activate()` (`./handlers/turns`),
 * {@link previewActivation} and {@link planningTurnState} share. Not exported from the package
 * index.
 *
 * A unit carrying `Braced` at its turn start is on its braced turn (`onBracedTurn`: the status
 * outlives only that one turn) and gets no standard move (RAW p.73: "you cannot ... move
 * normally"). A move an effect grants mid-turn still adds to the zero.
 */
export function freshTurnBudget(c: Pick<Combatant, "base" | "statuses">): TurnBudget {
  const move = hasStatus(c.statuses, BRACED_STATUS) ? 0 : c.base.speed;
  return { actions: ACTIONS_PER_TURN, move, actionUses: {}, protocolUses: {}, spent: { actions: 0, move: 0 } };
}

/**
 * **The pre-activation PREVIEW state** — `state` as it *would* read if `combatantId` activated
 * right now, for a combatant that is {@link perUnitState} `"ready"`; `state` itself, unchanged,
 * for anything else (not ready, not a combatant, already acting, wrong side, already acted,
 * destroyed, not an actor). Read-only, total, and pure.
 *
 * **THE NEVER-COMMIT INVARIANT.** This is a derivation for *reading*. It is **never committed,
 * never dispatched, never persisted, and never passed to the reducer as the state to apply
 * against.** The only state a command may be applied to is the room's real
 * `EncounterHistory.state`; the real transition is always the real `activate_unit` command,
 * through the real reducer, with its real hook chain. A caller that applies a command to *this*
 * object has already lost the property that makes it safe — that it can be computed anywhere,
 * any number of times, and thrown away.
 *
 * **What it is for — one concept, used twice, so the two can never disagree.** The encounter room
 * folds an `activate_unit` when a controlled, ready unit acts off-activation
 * (`projects/pre-activation-affordances.md` D1/D2); the gate ladder that decides whether that
 * action is *legal* has to run against the budget the fold would mint, not against the idle
 * `turn` the unit still carries. The same object is what the board's pre-activation affordances
 * (the ability bar's budget read, the movement-range overlay, the choosers) compose the
 * **existing, untouched** legality functions against — `reachableCells(previewActivation(state,
 * c), c)`, and so on. Because the display and the server's own fold read the *same* derivation,
 * the affordance and the wall agree by construction rather than by two hand-kept mirrors.
 *
 * **Why running the ladder against this is a proof, not a shortcut:** a fresh activation always
 * mints the FULL per-turn budget (actions to the allotment, move to Speed, empty use ledgers), so
 * nothing can pass here that a real activation would then have failed. The preview is the most
 * permissive state the fold can produce, and the fold produces exactly it.
 *
 * **It mirrors `activate()` (`./handlers/turns`) field for field** — the acting `turn` with its
 * fresh {@link "./types" TurnBudget}, the reaction-slot refresh, the held-action lapse, and the
 * `turnOrdinal` bump. That correspondence is load-bearing (a preview that under-states the
 * activation would let the ladder reject an action the fold makes legal; one that over-states it
 * would let an illegal action through). The budget comes from the shared {@link freshTurnBudget};
 * the rest is **pinned empirically**, not by shared code: `activationPreview.test.ts` asserts this
 * function's `activation` block is deep-equal to the one the real `activate_unit` reducer produces.
 */
export function previewActivation(state: EncounterState, combatantId: CombatantId): EncounterState {
  const c = state.combatants[combatantId];
  if (c === undefined) return state;
  if (perUnitState(state)[combatantId] !== "ready") return state;
  const a = state.activation;
  const budget = freshTurnBudget(c);
  // The LAPSE — `activate()` clears the activating unit's OWN leftover held action (RAW: an
  // untriggered hold is lost at the holder's next turn). Mirrored here because arm 1 of the
  // room's gate ladder (the held-action lockout) reads `heldActions`, so a preview that kept the
  // hold would reject an action the fold would have made legal.
  let heldActions = a.heldActions;
  if (heldActions[combatantId] !== undefined) {
    const { [combatantId]: _lapsed, ...rest } = heldActions;
    heldActions = rest;
  }
  return {
    ...state,
    activation: {
      ...a,
      reactionSlot: {},
      heldActions,
      turn: { phase: "acting", actor: combatantId, budget },
      turnOrdinal: a.turnOrdinal + 1,
    },
  };
}

/**
 * **The off-turn PLANNING state**: `state` as if `combatantId` had just started a fresh turn where it
 * stands, `turn: {phase: "acting", actor, budget}` with the {@link freshTurnBudget} mint. Unlike
 * {@link previewActivation} it answers for any placed actor, not only a `"ready"` one, because
 * off-turn a unit is usually `on_deck` or `activated`. Returns `state` unchanged for a non-actor or an
 * unplaced unit. Statuses are untouched, so `voluntaryMoveBar` (Immobilized, Stunned, Down and Out)
 * still empties the reach.
 *
 * Only `turn` is rewritten: no reaction-slot refresh, held-action lapse or `turnOrdinal` bump. It
 * feeds the movement wash, the planning drag's path and the move forecast, which read the actor's
 * budget and nothing else.
 *
 * **It inherits {@link previewActivation}'s NEVER-COMMIT INVARIANT.** It is never committed, never
 * dispatched, never persisted, and never passed to the reducer as the state to apply against. It is
 * a looser hypothetical than the preview (no legality ladder admits it), so a command applied to it
 * would be legal against a turn that does not exist.
 */
export function planningTurnState(state: EncounterState, combatantId: CombatantId): EncounterState {
  const c = state.combatants[combatantId];
  if (c === undefined || !isActor(c) || c.pos == null) return state;
  return {
    ...state,
    activation: {
      ...state.activation,
      turn: { phase: "acting", actor: combatantId, budget: freshTurnBudget(c) },
    },
  };
}
